import SelectMenu from "../../components/controls/SelectMenu";
import { ManufactureEmployeeViewModel } from "../../viewmodels/production/ManufactureEmployeeViewModel";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import * as actions from "../../store/actions/index";
import { convertToTwoDecimals, withRouter } from "../../shared/utility";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useForm } from "../../components/forms/useForm";
import Input from "../../components/controls/Input";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import EmptyModal from "../../components/modals/EmptyModal";
import ScannedItemAmountModal from "../../components/forms/production/ScannedItemAmountModal";
import ScannedItemEditDeleteModal from "../../components/forms/production/ScannedItemEditDeleteModal";
import EditScannedItemModal from "../../components/forms/production/EditScannedItemModal";
import ActionButton from "../../components/controls/ActionButton";

const initialFValues = {
  lot_number: "",
  scanned_assembly_barcode: "",
  quick_scan: false,
  scanned_items: [],
};

function ManufacturedProductsEmployee(props) {
  const [barcodeScanInProgress, setBarcodeScanInProgress] = useState(false);
  const [expandedRows, setExpandedRows] = useState(null);
  const { values, setValues, handleInputChange } = useForm(
    initialFValues,
    false,
  );
  const { logout, addSnackbar } = props;

  const {
    lotNumbers,
    setEmptyModelOpen,
    emptyModal,
    fetchLotNumbersForDropdown,
    getReceivingItemByBarcode,
    saveScannedItemToProduction,
    getProductScannedItems,
    showProgressModal,
    hideProgressModal,
    askDeleteScannedItem,
    updateScannedItem,
  } = ManufactureEmployeeViewModel(logout, addSnackbar);

  useEffect(() => {
    fetchLotNumbersForDropdown();
  }, []);

  const allowExpansion = (rowData) => {
    const receivingItems = rowData.receivingItems ? rowData.receivingItems : [];
    return receivingItems.length > 0;
  };

  const amountInUseTemplate = (rowData) => {
    return (
      <span>
        {rowData.amount_in_use} {rowData.unit_of_measure_label || ""}
      </span>
    );
  };

  const boxBarcodeLast9Template = (rowData) => {
    const boxBarcode = rowData.barcode;
    if (!boxBarcode) return "N/A";
    return boxBarcode.slice(-9);
  };

  const ingredientAmountTemplate = (rowData) => {
    return (
      <span>
        {convertToTwoDecimals(parseFloat(rowData.ingredient_amount_used))}{" "}
        {rowData.unit_of_measure || ""}
      </span>
    );
  };

  const handleEditSubItemConfirm = async (payload) => {
    const result = await updateScannedItem(payload);

    if (result && result.success) {
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      if (result.data && result.data.recipe_items) {
        setValues((prevValues) => ({
          ...prevValues,
          scanned_items: result.data.recipe_items,
        }));
      }
    }
  };

  const handleEditSubItem = (receivingItem) => {
    setEmptyModelOpen({
      childComponent: (
        <EditScannedItemModal
          receivingItem={receivingItem}
          production_id={values.lot_number.value}
          setOpen={setEmptyModelOpen}
          onConfirm={handleEditSubItemConfirm}
        />
      ),
      open: true,
    });
  };

  const handleDeleteSubItem = (receivingItem) => {
    askDeleteScannedItem(
      receivingItem.barcode,
      receivingItem,
      values.lot_number.value,
      (result) => {
        if (result && result.data && result.data.recipe_items) {
          setValues((prevValues) => ({
            ...prevValues,
            scanned_items: result.data.recipe_items,
          }));
        }
      },
    );
  };

  const subItemActionTemplate = (rowData) => {
    return (
      <ActionButton
        onClick={() => {
          setEmptyModelOpen({
            childComponent: (
              <ScannedItemEditDeleteModal
                scannedItem={rowData}
                setOpen={setEmptyModelOpen}
                onEdit={handleEditSubItem}
                onDelete={handleDeleteSubItem}
              />
            ),
            open: true,
          });
        }}
      >
        Edit
      </ActionButton>
    );
  };

  const rowExpansionTemplate = (data) => {
    return (
      <div className="p-0">
        <DataTable
          value={data.receivingItems || []}
          tableClassName="min-w-full bg-white divide-y divide-gray-300 manufacture-recipe-items-children-table"
          cellClassName="py-2 bg-gray-100 border-1-bottom-gray"
        >
          <Column header="Barcode (Last 9)" body={boxBarcodeLast9Template} />
          <Column
            header="Amount Used"
            body={ingredientAmountTemplate}
            style={{ textAlign: "left" }}
          />
          <Column
            header="Actions"
            body={subItemActionTemplate}
            style={{ width: "5rem" }}
          />
        </DataTable>
      </div>
    );
  };

  const handleModalConfirm = async (payload) => {
    const result = await saveScannedItemToProduction(payload);

    if (result && result.success) {
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      setValues((prevValues) => ({
        ...prevValues,
        scanned_assembly_barcode: "",
        scanned_items: result.data.recipe_items,
      }));
    } else {
      setValues((prevValues) => ({
        ...prevValues,
        scanned_assembly_barcode: "",
      }));
    }
  };

  const handleBarcodeScan = async (scannedBarcode) => {
    setBarcodeScanInProgress(true);

    const callResult = await getReceivingItemByBarcode(scannedBarcode);

    if (callResult) {
      if (callResult.success) {
        // Extract receiving item from the response
        const receivingItem =
          callResult.data?.receiving_item || callResult.data;

        const ingredientId = parseInt(receivingItem.ingredient_id, 10);
        const recipeItem = values.scanned_items.find((item) => {
          const bomId = parseInt(item.bom_id, 10);
          const rowIngredientId = parseInt(item.ingredient_id, 10);
          return (
            (!Number.isNaN(bomId) && bomId === ingredientId) ||
            (!Number.isNaN(rowIngredientId) && rowIngredientId === ingredientId)
          );
        });

        if (!recipeItem || recipeItem.amount_required == null) {
          setEmptyModelOpen({
            childComponent: (
              <ModelErrorComponent
                setOpen={setEmptyModelOpen}
                title={"Ingredient not in this production"}
                subtitle={
                  "No recipe line matches this barcode for the selected production. Pick the correct production or verify the BOM includes this ingredient."
                }
              />
            ),
            open: true,
          });
          setBarcodeScanInProgress(false);
          return;
        }

        let requiredAmount = parseFloat(recipeItem.amount_required);
        if (Number.isNaN(requiredAmount)) {
          requiredAmount = parseFloat(receivingItem.in_stock) || 0;
        }
        if (parseFloat(receivingItem.in_stock) < requiredAmount) {
          requiredAmount = parseFloat(receivingItem.in_stock);
        }
        setEmptyModelOpen({
          childComponent: (
            <ScannedItemAmountModal
              receivingItem={receivingItem}
              setOpen={setEmptyModelOpen}
              onConfirm={handleModalConfirm}
              lotNumber={values.lot_number}
              requiredAmount={requiredAmount}
            />
          ),
          open: true,
        });
      } else {
        setEmptyModelOpen({
          childComponent: (
            <ModelErrorComponent
              setOpen={setEmptyModelOpen}
              title={"Barcode not found"}
              subtitle={"No receiving item matched that barcode."}
            />
          ),
          open: true,
        });
      }
    } else {
      setEmptyModelOpen({
        childComponent: (
          <ModelErrorComponent
            setOpen={setEmptyModelOpen}
            title={"Barcode not found"}
            subtitle={"No receiving item matched that barcode."}
          />
        ),
        open: true,
      });
    }
    setBarcodeScanInProgress(false);
  };

  return (
    <div className="qatrace-screen qatrace-module-page px-4 sm:px-6 lg:px-8">
      <div>
        <SelectMenu
          name="lot_number"
          options={lotNumbers}
          title={"Lot Numbers"}
          value={values.lot_number}
          handleInputChange={async (e) => {
            handleInputChange(e);
            const response = await getProductScannedItems(e.target.value.value);
            if (response && response.recipe_items) {
              setValues((prevValues) => ({
                ...prevValues,
                scanned_items: response.recipe_items,
              }));
            } else {
              setValues((prevValues) => ({ ...prevValues, scanned_items: [] }));
            }
          }}
        />
      </div>

      {values.lot_number && (
        <div>
          <div className="py-4 mt-5">
            <div className="grid grid-cols-1 gap-2">
              <div className="col-span-1">
                <Input
                  label="Barcode Scanner"
                  name="scanned_assembly_barcode"
                  onChange={handleInputChange}
                  value={values.scanned_assembly_barcode}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleBarcodeScan(e.target.value);
                    }
                  }}
                  disabled={barcodeScanInProgress}
                  labelOn={true}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              {values.scanned_items && values.scanned_items.length > 0 ? (
                <div className="mt-4">
                  <DataTable
                    value={values.scanned_items}
                    expandedRows={expandedRows}
                    onRowToggle={(e) => {
                      setExpandedRows(e.data);
                    }}
                    rowExpansionTemplate={rowExpansionTemplate}
                    dataKey="id"
                    className="p-datatable-sm"
                  >
                    <Column field="bom_name" header="BOM Name" />
                    <Column header="Total in Use" body={amountInUseTemplate} />
                    <Column
                      expander={allowExpansion}
                      style={{ width: "5rem" }}
                    />
                  </DataTable>
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
      )}
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ManufacturedProductsEmployee),
);
